import type { BenLanguageCode } from '@ben/common/languages';
import { BEN_LANGUAGES } from '@ben/common/languages';
import Select from '@ben/orion/select';
import { memo, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface BaseLanguageSelectProps {
  panelContent?: ({
    languageCode,
  }: {
    languageCode: BenLanguageCode;
  }) => ReactNode;
  isCompact?: boolean;
  /**
   * When provided, the select is placed on the right of this content in a row (e.g. a label).
   */
  leftSlot?: ReactNode;
}

interface ControlledLanguageSelectProps extends BaseLanguageSelectProps {
  /**
   * Controlled mode - both languageCode and onChange must be provided together
   */
  languageCode: BenLanguageCode;
  onChange: (languageCode: BenLanguageCode) => void;
}

interface UncontrolledLanguageSelectProps extends BaseLanguageSelectProps {
  /**
   * Uncontrolled mode - neither languageCode nor onChange should be provided
   */
  languageCode?: never;
  onChange?: never;
}

export type LanguageSelectProps =
  | ControlledLanguageSelectProps
  | UncontrolledLanguageSelectProps;

const LanguageSelect = ({
  panelContent: PanelContent,
  isCompact = false,
  leftSlot,
  languageCode,
  onChange,
}: LanguageSelectProps) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<BenLanguageCode>('en');
  const { t } = useTranslation();

  const isControlled = languageCode !== undefined && onChange !== undefined;

  const handleChange = (newLanguageCode: BenLanguageCode) => {
    if (isControlled) {
      onChange(newLanguageCode);
    } else {
      setSelectedLanguage(newLanguageCode);
    }
  };

  const currentValue = isControlled ? languageCode : selectedLanguage;

  const languageOptions = useMemo(
    () =>
      BEN_LANGUAGES.map(({ code, label }) => ({
        value: code,
        label,
      })),
    []
  );

  const selectElement = (
    <SelectWrapper $inRow={Boolean(leftSlot)}>
      <Select
        data={languageOptions}
        value={currentValue}
        onChange={(value) => {
          if (value) {
            handleChange(value as BenLanguageCode);
          }
        }}
        size={isCompact ? 'xs' : 'sm'}
        allowDeselect={false}
        width="fit-content"
        aria-label={t('phrases.language')}
      />
    </SelectWrapper>
  );

  const contentPanel = PanelContent && (
    <ContentWrapper $isCompact={isCompact}>
      <PanelContent languageCode={currentValue} />
    </ContentWrapper>
  );

  if (leftSlot) {
    return (
      <>
        <HeaderRow>
          {leftSlot}
          {selectElement}
        </HeaderRow>
        {contentPanel}
      </>
    );
  }

  return (
    <>
      {selectElement}
      {contentPanel}
    </>
  );
};

export default memo(LanguageSelect);

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const SelectWrapper = styled.div<{ $inRow?: boolean }>`
  flex-shrink: 0;
  margin-bottom: ${({ theme, $inRow }) => ($inRow ? 0 : theme.spacing(2))};
`;

const ContentWrapper = styled.div<{ $isCompact: boolean }>`
  ${({ theme, $isCompact }) =>
    $isCompact &&
    `
      margin-top: ${theme.spacing(0.5)};
    `}
`;
